import { z } from 'zod';

const SEARCH_PATHS: Record<string, string> = {
  customers: 'Customer Search',
  search: 'Search',
  properties: 'Property Search',
  'jobs-unified': 'Job Search',
  hrDashboard: 'Performance Reviews',
};

/**
 * Records a page can go back to: a property opened from its customer's record, a customer or
 * property opened from a job, and an earlier review opened from a review.
 */
const RECORD_PATHS: Record<string, string> = {
  'customer-details': 'Customer',
  'job-details': 'Job',
  hrDashboard: 'Review',
};

const pathOf = (value: string) => value.match(/^\/([A-Za-z-]+)\/?(\?|$)/)?.[1];
const recordPathOf = (value: string) => value.match(/^\/([A-Za-z-]+)\/\d+\/?(\?|$)/)?.[1];

/**
 * A search to go back to, as a path with its query (`/customers?lastname=smith`), so its filters
 * come back too, or the customer record a property was opened from (`/customer-details/42`).
 * Only those routes are accepted, so the param can't send anyone elsewhere.
 */
export const searchReturnTo = z
  .string()
  .refine((value) => {
    const path = pathOf(value);
    if (path) return path in SEARCH_PATHS;
    const recordPath = recordPathOf(value);
    return !!recordPath && recordPath in RECORD_PATHS;
  })
  .optional()
  .catch(undefined);

/**
 * "Back to Customer Search", "Back to Search", "Back to Property Search" or "Back to Job Search";
 * "Back to Customer" for a customer record (the record names the customer when it knows them).
 */
export const searchReturnLabel = (returnTo: string) => {
  const recordPath = recordPathOf(returnTo);
  if (recordPath && recordPath in RECORD_PATHS) return `Back to ${RECORD_PATHS[recordPath]}`;
  return `Back to ${SEARCH_PATHS[pathOf(returnTo) ?? ''] ?? 'Search'}`;
};

/**
 * True when `returnTo` is a record (a customer or a job) rather than a search. Back to a record
 * steps back through history when it can, so that record keeps its own Back and scroll.
 */
export const isRecordReturn = (returnTo: string) => {
  const recordPath = recordPathOf(returnTo);
  return !!recordPath && recordPath in RECORD_PATHS;
};

/** True when `returnTo` is a customer record, so the page can name that customer in Back. */
export const isCustomerRecordReturn = (returnTo: string) =>
  recordPathOf(returnTo) === 'customer-details';

/** The job record as a place to come back to. */
export const jobRecordHref = (eventId: number) => `/job-details/${eventId}`;

/** The customer record as a place to come back to. */
export const customerRecordHref = (customerId: number) => `/customer-details/${customerId}`;

/** This page as a path and query, for a record's Back to return to with the filters kept. */
export const currentSearchHref = () => `${window.location.pathname}${window.location.search}`;
